<template>
  <div class="organizer-picker">
    <label class="field-label">Организаторы</label>
    <p class="field-hint">Вы (создатель) будете указаны организатором автоматически.</p>

    <div class="organizer-section">
      <p class="section-label">Пользователи сайта</p>
      <div class="organizer-search">
        <input
          v-model="query"
          type="text"
          class="input"
          placeholder="Поиск по @username"
          autocomplete="off"
          @input="onSearch"
        />
        <ul v-if="suggestions.length" class="organizer-suggestions">
          <li v-for="user in suggestions" :key="user.id">
            <button type="button" class="organizer-suggestion" @click="addUser(user)">
              {{ userDisplayName(user) }}
            </button>
          </li>
        </ul>
      </div>
      <div v-if="selectedUsers.length" class="organizer-chips">
        <span v-for="user in selectedUsers" :key="`user-${user.id}`" class="organizer-chip">
          <UserIdentity :user="user" size="sm">{{ userDisplayName(user) }}</UserIdentity>
          <button type="button" class="organizer-remove" aria-label="Удалить" @click="removeUser(user.id)">
            ×
          </button>
        </span>
      </div>
    </div>

    <div class="organizer-section">
      <p class="section-label">Внешние организаторы</p>
      <div class="external-form">
        <input
          v-model="externalName"
          type="text"
          class="input"
          placeholder="ФИО"
          autocomplete="off"
        />
        <input
          v-model="externalContact"
          type="text"
          class="input"
          placeholder="Контакт (email, телефон, telegram)"
          autocomplete="off"
        />
        <button type="button" class="btn btn-secondary btn-sm" @click="addExternal">
          Добавить
        </button>
      </div>
      <p v-if="externalError" class="error-message">{{ externalError }}</p>
      <div v-if="selectedExternal.length" class="organizer-chips">
        <span
          v-for="(item, index) in selectedExternal"
          :key="externalKey(item, index)"
          class="organizer-chip organizer-chip--external"
        >
          <span>{{ item.name }} · {{ item.contact }}</span>
          <button
            type="button"
            class="organizer-remove"
            aria-label="Удалить"
            @click="removeExternal(index)"
          >
            ×
          </button>
        </span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'
import UserIdentity from './UserIdentity.vue'
import { searchUsers } from '../api/trips'
import { userDisplayName } from '../utils/userDisplay'

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
})

const emit = defineEmits(['update:modelValue'])

const query = ref('')
const suggestions = ref([])
const selectedUsers = ref([])
const selectedExternal = ref([])
const externalName = ref('')
const externalContact = ref('')
const externalError = ref('')
let searchTimeout = null

function syncFromModelValue(value) {
  selectedUsers.value = value.filter((item) => item.user).map((item) => item.user)
  selectedExternal.value = value
    .filter((item) => !item.user)
    .map((item) => ({
      id: item.id,
      name: item.name,
      contact: item.contact,
    }))
}

watch(
  () => props.modelValue,
  (value) => {
    syncFromModelValue(value)
  },
  { deep: true, immediate: true }
)

function emitValue() {
  emit(
    'update:modelValue',
    [
      ...selectedUsers.value.map((user) => ({ user })),
      ...selectedExternal.value.map((item) => ({
        id: item.id,
        name: item.name,
        contact: item.contact,
      })),
    ]
  )
}

function onSearch() {
  clearTimeout(searchTimeout)
  const value = query.value.trim()
  if (value.length < 2) {
    suggestions.value = []
    return
  }

  searchTimeout = setTimeout(async () => {
    try {
      const { data } = await searchUsers(value.replace(/^@/, ''))
      const selectedIds = new Set(selectedUsers.value.map((user) => user.id))
      suggestions.value = data.filter((user) => !selectedIds.has(user.id))
    } catch {
      suggestions.value = []
    }
  }, 250)
}

function addUser(user) {
  if (selectedUsers.value.some((item) => item.id === user.id)) return
  selectedUsers.value = [...selectedUsers.value, user]
  emitValue()
  query.value = ''
  suggestions.value = []
}

function removeUser(userId) {
  selectedUsers.value = selectedUsers.value.filter((user) => user.id !== userId)
  emitValue()
}

function addExternal() {
  externalError.value = ''
  const name = externalName.value.trim()
  const contact = externalContact.value.trim()
  if (!name || !contact) {
    externalError.value = 'Укажите ФИО и контакт'
    return
  }

  selectedExternal.value = [...selectedExternal.value, { name, contact }]
  externalName.value = ''
  externalContact.value = ''
  emitValue()
}

function removeExternal(index) {
  selectedExternal.value = selectedExternal.value.filter((_, itemIndex) => itemIndex !== index)
  emitValue()
}

function externalKey(item, index) {
  return item.id ? `external-${item.id}` : `external-new-${index}`
}
</script>

<style scoped>
.organizer-picker {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.field-label {
  font-weight: 600;
}

.field-hint {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 400;
  color: #64748b;
}

.organizer-section {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.section-label {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 600;
  color: #334155;
}

.organizer-search {
  position: relative;
}

.organizer-suggestions {
  position: absolute;
  z-index: 10;
  top: 100%;
  left: 0;
  right: 0;
  list-style: none;
  margin: 0.25rem 0 0;
  padding: 0;
  background: #fff;
  border: 1px solid #cbd5e1;
  border-radius: 0.375rem;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08);
}

.organizer-suggestion {
  display: block;
  width: 100%;
  text-align: left;
  padding: 0.5rem 0.75rem;
  border: none;
  background: transparent;
  cursor: pointer;
}

.organizer-suggestion:hover {
  background: #f1f5f9;
}

.external-form {
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  gap: 0.5rem;
}

.organizer-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.organizer-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.5rem;
  background: #f1f5f9;
  border-radius: 999px;
}

.organizer-chip--external {
  font-size: 0.875rem;
}

.organizer-remove {
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 1.125rem;
  line-height: 1;
  color: #64748b;
}

.error-message {
  margin: 0;
  color: #dc2626;
  font-size: 0.875rem;
}

@media (max-width: 640px) {
  .external-form {
    grid-template-columns: 1fr;
  }
}
</style>
